import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Auth, cqAPI, getBid } from "../../lib/Auth";
import SendArea from "./SendArea";
import Messages from "./Messages";
import UserInfo from "./UserInfo";
import "./Inbox.css";
import { useColorMode, useToast } from "@chakra-ui/react";
import { useAppContext } from "../../AppContext";
const _ = require("lodash");

const DRAFT_FETCH_LIMIT = 5;

function InboxMessages({ removeActionBarItem, addActionBarItem, isMobile }) {
  const { uid } = useParams();
  const bid = getBid();
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [disableSendButton, setDisableSendButton] = useState(false);
  const [messageType, setMessageType] = useState([]);
  const toast = useToast();
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [relatedUid, setRelatedUid] = useState(uid);
  const { colorMode } = useColorMode();
  const {
    staff,
    setStaff,
    realtime,
    realtimeInitialized,
    location,
    isPatient,
    handleDraftButtonClick,
  } = useAppContext();

  const [userInfo, setUserInfo] = useState({});
  const [draft, setDraft] = useState("");
  const [isDraftBoxOpen, setIsDraftBoxOpen] = useState(false);
  const [conversationClosed, setConversationClosed] = useState(false);
  const [assigned, setAssigned] = useState("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const typingTimer = useRef(null);

  const fetchAllMessages = async (update = false, p = 0) => {
    const response = await cqAPI.get(`/messages?from=${uid}&page=${p}`);
    if (response.type === "error") {
      toast({
        title: 'Error',
        description: response.msg,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    const fetched = response.data.conversations || [];

    // If we have current messages, we will do a union, ensuring no duplicates
    // based on _id. The direction of merging depends on whether this is page 0 (latest)
    // or a subsequent page (older messages).
    const combined = p === 0? _.uniqBy([...messages, ...fetched], '_id') : _.uniqBy([...fetched, ...messages], '_id');
    
    setMessages(combined);

    if (fetched.length === 0 && p > 0) {
      // No more older messages
      setHasMore(false);
    }

    setAssigned(response.data.assigned);

    if (window.location.pathname.startsWith("/task")) {
      const related_uid = fetched.find(c => c.related_uid)?.related_uid
      setRelatedUid(related_uid ? (/^0{24}$/.test(related_uid) ? null : related_uid) : null);
    } else if (window.location.pathname.startsWith("/inbox")) {
      setRelatedUid(relatedUid ? (/^0{24}$/.test(relatedUid) ? null : relatedUid) : null);
    }

    // Draft logic remains unchanged
    if (fetched.length && fetched[fetched.length - 1].type === 'draft') {
      setDraft(fetched[fetched.length - 1].body);
      setIsDraftBoxOpen(true);
    }
    setUserInfo({
      ...userInfo,
      ...response.data.userInfo
    });
    const conversationOnly = combined.filter(c => 'closed' in c);
    setConversationClosed(
      conversationOnly.length
        ? conversationOnly[conversationOnly.length - 1].closed
        : false
    );

    const types = [];
    if (response.data.userInfo?.possible_notification_pref?.indexOf("email") !== -1) {
      types.push("email");
    }
    if (response.data.userInfo?.possible_notification_pref?.indexOf("sms") !== -1) {
      types.push("sms");
    }
    if (!update && p === 0) setMessageType(types);

    if (!staff.length) {
      const s = await cqAPI.get("/list-staff");
      if (s.data && s.data.length) {
        setStaff(s.data);
      }
    }

    return true;
  };

  const showTyping = useCallback((payload) => {
    if (payload.data.uid === Auth.user.id) return;
    if (payload.data.target_uid !== uid) return;
    if (typingTimer.current) clearTimeout(typingTimer.current);
    setTyping(true);
    typingTimer.current = setTimeout(() => {
      setTyping(false);
      typingTimer.current = null;
    }, 5000);
  }, [typingTimer]);

  const fetchLastDraftMessage = async () => {
    try {
      const response = await cqAPI.get(`/messages/draft?from=${uid}`);
      if (response?.data?.body) {
        return String(response.data.body);
      }
    } catch (error) {
      console.log("error fetching draft message: ", error);
    }
    return "";
  };

  const updateDraft = async () => {
    const lastMessage = messages[messages.length - 1];
    if (
      lastMessage?.type !== undefined &&
      lastMessage.type !== "call" &&
      lastMessage?.direction !== undefined &&
      lastMessage.direction !== "internal_note" &&
      lastMessage.direction !== "outgoing"
    ) {
      const tmpDraft = await fetchLastDraftMessage();
      if (!_.isEqual(tmpDraft, draft)) {
        setDraft(tmpDraft);
        setIsDraftBoxOpen(true);
      }
    }
    // draft already sent
    if (lastMessage?.type !== "draft" && lastMessage?.direction === "outgoing") {
      setIsDraftBoxOpen(false);
      setDraft("");
    }
  };

  useEffect(() => {
    let counter = 0;
    const draftIntervalId = setInterval(async () => {
      if (counter >= DRAFT_FETCH_LIMIT) {
        clearInterval(draftIntervalId);
        return;
      }
      await updateDraft();
      counter += 1;
    }, 3000);
    return () => {
      clearInterval(draftIntervalId);
    };
  }, [messages]);

  const handleNewMessage = async (update) => {
    // Reset to first page on new message
    setPage(0);
    setHasMore(true);
    await fetchAllMessages(update, 0);
    await updateDraft();
  };

  useEffect(() => {
    if (!uid) return;
    setPage(0);
    setHasMore(true);
    fetchAllMessages(false, 0);
  }, [uid]);

  useEffect(() => {
    if (!realtime.current) return;
    const UpdateTopics = [
      `conv_status_change/${bid}/${uid}`,
      `_new_chat_message/${bid}/${uid}`,
    ];
    UpdateTopics.forEach((topic) =>
      realtime.current.subscribeToTopic(topic, bid, handleNewMessage)
    );
    realtime.current.subscribeToTopic(
      `chat_typing/${bid}/${uid}`,
      bid,
      showTyping
    );
  }, [realtimeInitialized, uid, realtime, showTyping]);

  useEffect(() => {
    const current = realtime?.current;
    return () => {
      if (!current) return;
      const UpdateTopics = [
        `conv_status_change/${bid}/${uid}`,
        `_new_chat_message/${bid}/${uid}`,
      ];
      UpdateTopics.forEach((topic) =>
        current.unsubscribeFromTopic(topic, bid, () =>
          handleNewMessage(true)
        )
      );
      current.unsubscribeFromTopic(
        `chat_typing/${bid}/${uid}`,
        bid,
        showTyping
      );
    };
  }, [location, realtime, showTyping]);

  const loadMoreMessages = async (oldScrollHeight, oldScrollTop, scrollContainer) => {
    if (!hasMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    const success = await fetchAllMessages(true, nextPage);
    if (success) {
      setPage(nextPage);
      // Restore scroll position
      // After React renders new messages, wait a frame:
      requestAnimationFrame(() => {
        const newScrollHeight = scrollContainer.scrollHeight;
        // Adjust scroll so user stays at the same position relative to the messages
        scrollContainer.scrollTop = newScrollHeight - oldScrollHeight + oldScrollTop;
      });
    }
    setIsLoadingMore(false);
  };

  return (
    <div className="inbox-container">
      <div className="chat-area">
        <Messages
          messages={messages}
          disableSendButton={disableSendButton}
          userInfo={userInfo}
          colorMode={colorMode}
          isPatient={isPatient}
          fetchAllMessages={fetchAllMessages}
          setMessages={setMessages}
          isMobile={isMobile}
          hasMore={hasMore}
          onLoadMore={loadMoreMessages}
          isLoadingMore={isLoadingMore}
          page={page}
        />

        <SendArea
          isPatient={isPatient}
          disableSendButton={uid === "000000000000000000000000" || disableSendButton}
          userInfo={userInfo}
          toast={toast}
          setUserInfo={setUserInfo}
          messageType={messageType}
          setMessageType={setMessageType}
          typing={typing}
          colorMode={colorMode}
          uid={uid}
          isStripeConnected={isStripeConnected}
          setIsStripeConnected={setIsStripeConnected}
          setDisableSendButton={setDisableSendButton}
          staff={staff}
          draft={draft}
          setDraft={setDraft}
          handleDraftButtonClick={handleDraftButtonClick}
          isDraftBoxOpen={isDraftBoxOpen}
          setIsDraftBoxOpen={setIsDraftBoxOpen}
          isMobile={isMobile}
        />
      </div>
      {!isPatient && (
        <UserInfo
          toast={toast}
          assigned={assigned}
          setAssigned={setAssigned}
          userInfo={userInfo}
          uid={uid}
          relatedUid={relatedUid}
          isStripeConnected={isStripeConnected}
          conversationClosed={conversationClosed}
          setConversationClosed={setConversationClosed}
          messages={messages}
          setUserInfo={setUserInfo}
          addActionBarItem={addActionBarItem}
          removeActionBarItem={removeActionBarItem}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

export default InboxMessages;
